import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import ManageUsers from "../../features/manage-users/ManageUsers";
import CustomTab from "../../components/custom-tab/CustomTab";
import ManagePatients from "../../features/manage-patients/ManagePatients";
import ManageMilestones from "../../features/manage-milestones/ManageMilestones";
import {
  TAB_LABEL_MANAGE_MILESTONES,
  TAB_LABEL_MANAGE_PATIENTS,
  TAB_LABEL_MANAGE_REDEMPTIONS,
  TAB_LABEL_MANAGE_USERS,
  TAB_LABEL_REPORTS,
  REDEMPTION_ADMIN,
} from "../../utility/constants/locale";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { adminTabsActions } from "./services/AdminTabsDataSlice";
import ManageRedemptions from "../../features/manage-redemptions/ManageRedemptions";
import { useGetUserListQuery } from "../../features/manage-users/GetUserListAPISlice";
import { formatDate } from "../../utility/functions/common-functions";
import {
  SUPER_ADMIN_DASHBOARD_TABS,
  ADMIN_DASHBOARD_TABS,
} from "../../utility/constants/common-constants";
import CustomSpinner from "../../components/custom-spinner/CustomSpinner";
import ApiErrorComponent from "../../components/api-error-component/ApiErrorComponent";

// Define the User interface to keep the data structure clear
interface User {
  displayName: string;
  emailId: string;
  createdDateTime: string;
  userRoleCode: string;
  status: string;
  location: string;
  userId: number;
  statusId: number;
  userRoleId: number;
}

interface Error {
  status: string;
}

const AdminDashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const [tabs, setTabs] = useState([]);
  const [currentTab, setCurrentTab] = useState(
    useAppSelector((state) => state.adminTabs.activeTab)
  );
  const adminRoleDescriptionData = useAppSelector(
    (state) => state.adminMasterData.adminRoleDesciption
  );

  const accessToken =
    useAppSelector((state) => state.adminAuth.user?.accessToken) ?? null;
  const userEmail =
    useAppSelector((state) => state.adminAuth.user?.email) ?? null;
  const [userRoleCode, setUserRoleCode] = useState(" ");
  const { data, error, isLoading } = useGetUserListQuery({ accessToken });

  // Use useMemo to format data for the table
  const formatDataForTable = useMemo(() => {
    return (
      data &&
      data.adminUsers.map(({ createdDateTime, ...rest }: User) => ({
        ...rest,
        formattedDate: formatDate(createdDateTime),
      }))
    );
  }, [data]);

  const findUserInAdminList = useMemo(() => {
    return formatDataForTable?.filter(
      (item: User) => item.emailId?.toLowerCase() === userEmail?.toLowerCase()
    );
  }, [formatDataForTable, userEmail]);

  // Use useEffect to update userRoleCode
  useEffect(() => {
    if (findUserInAdminList && findUserInAdminList.length > 0) {
      setUserRoleCode(
        findUserInAdminList[0]?.userRoleCode.toLowerCase() || "redemptionadmin"
      );
    }
  }, [findUserInAdminList]);

  const getRoleBasedAccessList = useMemo(() => {
    if (userRoleCode.toLowerCase() === REDEMPTION_ADMIN.toLowerCase()) {
      dispatch(adminTabsActions.setActiveTab(TAB_LABEL_MANAGE_REDEMPTIONS));
      setCurrentTab(TAB_LABEL_MANAGE_REDEMPTIONS);
    }

    return adminRoleDescriptionData?.filter(
      (item: any) => item?.roleName.toLowerCase() === userRoleCode.toLowerCase()
    );
  }, [userRoleCode]);

  if (error) {
    // Check for specific status code (e.g., 403)
    if ("status" in error) {
      if (error?.status === 403) {
        return <ApiErrorComponent errorCode={error?.status} />;
      }
    }
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    dispatch(adminTabsActions.setActiveTab(newValue));
    setCurrentTab(newValue);
  };

  return (
    <div>
      {findUserInAdminList && (
        <CustomTab
          tabs={
            userRoleCode.toLowerCase() === REDEMPTION_ADMIN.toLowerCase()
              ? ADMIN_DASHBOARD_TABS
              : SUPER_ADMIN_DASHBOARD_TABS
          }
          value={currentTab}
          handleTabChange={handleTabChange}
        />
      )}

      <Box sx={{ padding: 4 }}>
        {formatDataForTable &&
          getRoleBasedAccessList &&
          currentTab === TAB_LABEL_MANAGE_USERS && (
            <ManageUsers formatDataForTable={formatDataForTable} />
          )}

        {isLoading && <CustomSpinner />}

        {currentTab === TAB_LABEL_MANAGE_PATIENTS && <ManagePatients />}
        {currentTab === TAB_LABEL_MANAGE_REDEMPTIONS && <ManageRedemptions />}
        {getRoleBasedAccessList &&
          currentTab === TAB_LABEL_MANAGE_MILESTONES && <ManageMilestones />}
        {currentTab === TAB_LABEL_REPORTS && (
          <Typography sx={{ fontWeight: 500 }}>Reports- coming soon</Typography>
        )}
      </Box>
    </div>
  );
};

export default AdminDashboard;
